import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";

import useClozeSelection from "../QuickCapture/useClozeSelection";
import { clozeTextFor } from "./cloze";

// Add one lyric line to a custom collection, without leaving the station.
//
// Quick Capture is the neighbouring idea but the wrong shape here: it exists to
// turn a phrase into a generated sentence, and we already have the sentence -
// written, translated, and with a cloze the listener has just heard. So this
// form starts filled in and the work is editing, not authoring.
//
// Both fields are editable because the automatic cloze picks the longest word,
// which is a decent guess and regularly not the word you wanted to practise.

const NEW_COLLECTION = "new";

function csrfToken() {
  const meta = document.querySelector("meta[name=\"csrf-token\"]");
  return meta ? meta.getAttribute("content") : "";
}

// Every api/v1 request needs this header: the base controller raises BadRequest
// without it, before any action runs. packs/application.js sets it globally for
// jQuery, which fetch never sees - hence setting it again here.
//
// moment().utcOffset() is minutes east of UTC and getTimezoneOffset() is minutes
// behind it, so the sign flips.
function apiHeaders(extra) {
  return {
    "Time-Zone-Offset-Hours": String(-new Date().getTimezoneOffset() / 60),
    "X-Requested-With": "XMLHttpRequest",
    ...extra,
  };
}

function postJson(url, body) {
  return fetch(url, {
    body: JSON.stringify(body),
    credentials: "same-origin",
    headers: apiHeaders({ "Content-Type": "application/json", "X-CSRF-Token": csrfToken() }),
    method: "POST",
  });
}

export default function AddToCollection({
  collectionsUrl, defaultCollectionName, isPro, line, onClose, onSaved, proUrl, signInUrl,
  signedIn,
}) {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(NEW_COLLECTION);
  const [newName, setNewName] = useState(defaultCollectionName);
  const [text, setText] = useState(() => clozeTextFor(line));
  const [translation, setTranslation] = useState(line.translation || "");
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);
  const [error, setError] = useState(null);
  // Highlight a range, or double-click a word, to make it the cloze - the same
  // behaviour the Quick Capture textareas have, from the same hook.
  const { onSelect: onClozeSelect, ref: textRef } = useClozeSelection(setText);

  const editable = signedIn && isPro;

  // Escape closes, which also resumes playback - the parent owns that.
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (editable && textRef.current) textRef.current.focus();
  }, [editable]);

  // Only the user's own collections can be written to, so that's all this asks
  // for. A signed-out or free visitor never triggers the request.
  useEffect(() => {
    if (!editable) return undefined;

    let cancelled = false;

    fetch(`${collectionsUrl}?filter=mine&order=updatedAt`, {
      credentials: "same-origin",
      headers: apiHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) return;

        const mine = data.collections || [];
        setCollections(mine);
        // Land on the most recently touched collection when there is one, so
        // adding several lines in a row is one click each.
        if (mine.length) setSelected(String(mine[0].id));
      })
      .catch(() => { if (!cancelled) setError("Couldn't load your collections."); });

    return () => { cancelled = true; };
  }, [collectionsUrl, editable]);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    if (!editable || saving) return;

    setSaving(true);
    setError(null);

    try {
      let collection = collections.find((item) => String(item.id) === selected);

      if (!collection) {
        const created = await postJson(collectionsUrl, { collection: { name: newName.trim() } });
        const payload = await created.json();
        // create redirects to show on success and returns errors inline on
        // failure, so the presence of a collection is what distinguishes them.
        if (!payload.collection) throw new Error((payload.errors || ["Couldn't create that collection."]).join(", "));

        collection = payload.collection;
        setCollections((current) => [collection, ...current]);
        setSelected(String(collection.id));
      }

      const response = await postJson(collection.collectionClozeSentencesUpsertUrl, {
        collection_cloze_sentence: { text: text.trim(), translation: translation.trim() },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.errors) throw new Error(payload.errors || "Couldn't add that line.");

      setSavedTo(collection);
      onSaved();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }, [collections, collectionsUrl, editable, newName, onSaved, saving, selected, text, translation]);

  return (
    <div className="ir-modal-backdrop" onClick={onClose} role="presentation">
      {/* Stops a click inside the panel from reaching the backdrop's close. */}
      <div
        aria-label="Add this line to a collection"
        aria-modal="true"
        className="ir-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="ir-modal-head">
          <h2 className="ir-modal-title">Add to a collection</h2>
          <button aria-label="Close" className="ir-modal-close" onClick={onClose} type="button">&times;</button>
        </div>

        {savedTo ? (
          <div className="ir-modal-body">
            <p className="ir-modal-saved">
              Added to <strong>{savedTo.name}</strong>.
            </p>
            <div className="ir-modal-actions">
              <a className="ir-modal-btn-ghost" href={savedTo.playWebUrl}>Play the collection</a>
              <button className="ir-modal-btn" onClick={onClose} type="button">Back to the music</button>
            </div>
          </div>
        ) : (
          <form className="ir-modal-body" onSubmit={submit}>
            {/* Both gates at once. Saving needs an account AND Pro, and finding
                that out in two steps - sign up, then discover the second wall -
                is how an honest limit starts reading as a bait and switch. */}
            {!signedIn && (
              <p className="ir-modal-notice">
                <span className="joystix">Clozemaster</span> gets you fluent through sentences
                rather than word lists &mdash; fill in the missing word, in context, until it
                sticks. Saving lyrics into your own collection needs an account with{" "}
                <a href={proUrl}>Pro</a>.{" "}
                <a href={signInUrl}>Create a free account</a> to try it first.
              </p>
            )}

            {signedIn && !isPro && (
              <p className="ir-modal-notice">
                Custom collections are a <span className="joystix">Clozemaster Pro</span> feature.{" "}
                <a href={proUrl}>See what Pro includes</a> to save lyrics as you listen.
              </p>
            )}

            <label className="ir-modal-label" htmlFor="ir-add-text">
              Sentence
              <span className="ir-modal-hint">Select a word to make it the cloze</span>
            </label>
            <textarea
              className="ir-modal-input"
              disabled={!editable}
              id="ir-add-text"
              onChange={(event) => setText(event.target.value)}
              onSelect={onClozeSelect}
              ref={textRef}
              rows={2}
              value={text}
            />

            <label className="ir-modal-label" htmlFor="ir-add-translation">Translation</label>
            <textarea
              className="ir-modal-input"
              disabled={!editable}
              id="ir-add-translation"
              onChange={(event) => setTranslation(event.target.value)}
              rows={2}
              value={translation}
            />

            <label className="ir-modal-label" htmlFor="ir-add-collection">Collection</label>
            <select
              className="ir-modal-input"
              disabled={!editable}
              id="ir-add-collection"
              onChange={(event) => setSelected(event.target.value)}
              value={selected}
            >
              {collections.map((collection) => (
                <option key={collection.id} value={String(collection.id)}>{collection.name}</option>
              ))}
              <option value={NEW_COLLECTION}>New collection&hellip;</option>
            </select>

            {selected === NEW_COLLECTION && (
              <input
                aria-label="New collection name"
                className="ir-modal-input"
                disabled={!editable}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Collection name"
                type="text"
                value={newName}
              />
            )}

            {error && <p className="ir-modal-error">{error}</p>}

            <div className="ir-modal-actions">
              <button className="ir-modal-btn-ghost" onClick={onClose} type="button">Cancel</button>
              <button className="ir-modal-btn" disabled={!editable || saving} type="submit">
                {saving ? "Adding…" : "Add"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

AddToCollection.propTypes = {
  collectionsUrl: PropTypes.string,
  defaultCollectionName: PropTypes.string,
  isPro: PropTypes.bool,
  line: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  proUrl: PropTypes.string,
  signInUrl: PropTypes.string,
  signedIn: PropTypes.bool,
};

AddToCollection.defaultProps = {
  collectionsUrl: null,
  defaultCollectionName: "My Custom Collection",
  isPro: false,
  onSaved: () => {},
  proUrl: "/pro",
  signInUrl: "/users/sign_up",
  signedIn: false,
};
